import ToastComponent from "@components/common/ui/Toast";
import ToastContainer from "@components/common/ui/Toast/Container";
import { createContext, useContext, useState } from "react";

export interface ToastProps {
  id?: string;
  title: string;
  description?: string;
  type?: "success" | "error" | "info";
  timeout?: number;
}

interface ToastContextState {
  toast: (toast: ToastProps) => void;
}

const ToastContext = createContext<ToastContextState>({} as ToastContextState);

export const useToastContext = () => {
  const { toast } = useContext(ToastContext);
  return toast;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export default function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Record<string, ToastProps>>({});
  const toastDuration = 5000;

  const removeToast = (id: string) => {
    setToasts(toasts => {
      const { [id]: _, ...rest } = toasts;
      return rest;
    });
  };

  const toast = (toast: ToastProps) => {
    const id = toast.id ?? new Date().getTime().toString();
    setToasts(toasts => ({ ...toasts, [id]: toast }));
    setTimeout(() => removeToast(id), toast.timeout ?? toastDuration);
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      <ToastContainer>
        {Object.keys(toasts)
          .reverse()
          .map(key => {
            const { title, description, type } = toasts[key];
            return <ToastComponent key={key} {...{ title, description, type }} onClose={() => removeToast(key)} />;
          })}
      </ToastContainer>
      {children}
    </ToastContext.Provider>
  );
}
